import React from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import Checkbox from "@material-ui/core/Checkbox";
import Alert from "@material-ui/lab/Alert";

import Box from "@material-ui/core/Box";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import { makeStyles } from "@material-ui/core";
import { message } from "constants/message";

const useStyles = makeStyles((theme) => ({
  accordionWrapper: {
    marginTop: theme.spacing(1),
  },
  accordion: {
    padding: theme.spacing(1),
  },
  wrapper: {
    marginTop: theme.spacing(1),
  },
  countDef: {
    color: "#000"
  },
  countErr: {
    color: "#f00"
  },
}));

export default function CollectionKeyword({
  state,
  presetKeywords = [],
  maxWordsOr,
  maxWordsAnd,
  maxWordsNot,
  changeWordsOr,
  changeIgnoreCaseAnd,
  changeWordsAnd,
  changeIgnoreCaseNot,
  changeWordsNot,
  changeMentions,
}) {
  const classes = useStyles();
  const messages = message();

  /**
   * キーワード数をカウントする共通関数
   *
   * バックエンド（backend/app/models/keyword.rb）の Keyword.count_words と同じロジック：
   *   1. 改行で分割し、空行を除外して各行を取得する
   *   2. 各行をさらにカンマ(,)で分割する
   *   3. 末尾カンマは空要素を生むためトリムしてから分割する
   *   4. 分割後の全要素数を合計する
   *
   * 例: "apple,orange\nbanana\ngrape,melon,kiwi"
   *   → 行分割: ["apple,orange", "banana", "grape,melon,kiwi"]
   *   → カンマ分割: ["apple","orange"] + ["banana"] + ["grape","melon","kiwi"]
   *   → 合計: 2 + 1 + 3 = 6
   *
   * ※ 収集キーワード・絞り込みキーワード・除外キーワードすべてこの方法でカウントする
   *   （バックエンドが3種類すべて同じ Keyword.count_words で検証しているため）
   */
  const countKeywords = (words) => {
    if (!words || words === "") return 0;

    const rows = words.split("\n").filter((v) => v);
    let count = 0;

    rows.forEach((row) => {
      // 末尾カンマを除去してからカンマ分割し、要素数をカウント
      const trimmed = row.replace(/,$/g, "");
      count += trimmed.split(",").length;
    });

    return count;
  };

  // キーワード数をオーバーしたときに表示するエラーメッセージを設定する
  const setOverMaxMessage = (maxWords, wordsCount, setIsOverMaxWords, setErrorMessageOverMaxWords, errorMessage) => {
    if (maxWords < wordsCount) {
      setIsOverMaxWords(true);
      setErrorMessageOverMaxWords(errorMessage);
    }
    else {
      setIsOverMaxWords(false);
      setErrorMessageOverMaxWords("");
    }
  }

  // 収集キーワード数のリアルタイム表示とエラーメッセージ表示の設定
  const [wordsOrCount, setWordsOrCount] = React.useState(0);
  const [isOverMaxWordsOr, setIsOverMaxWordsOr] = React.useState(false);
  const [errorMessageOverMaxWordsOr, setErrorMessageOverMaxWordsOr] = React.useState("");

  React.useEffect(() => {
    setWordsOrCount(countKeywords(state.words_or));
  }, [state.words_or]);

  React.useEffect(() => {
    setOverMaxMessage(maxWordsOr, wordsOrCount, setIsOverMaxWordsOr, setErrorMessageOverMaxWordsOr, messages.errorMsg.overMaxWordsOr);
  }, [wordsOrCount, maxWordsOr]);

  // 絞り込みキーワード数のリアルタイム表示とエラーメッセージ表示の設定
  const [wordsAndCount, setWordsAndCount] = React.useState(0);
  const [isOverMaxWordsAnd, setIsOverMaxWordsAnd] = React.useState(false);
  const [errorMessageOverMaxWordsAnd, setErrorMessageOverMaxWordsAnd] = React.useState("");

  React.useEffect(() => {
    setWordsAndCount(countKeywords(state.words_and));
  }, [state.words_and]);

  React.useEffect(() => {
    setOverMaxMessage(maxWordsAnd, wordsAndCount, setIsOverMaxWordsAnd, setErrorMessageOverMaxWordsAnd, messages.errorMsg.overMaxWordsAnd);
  }, [wordsAndCount, maxWordsAnd]);

  // 除外キーワード数のリアルタイム表示とエラーメッセージ表示の設定
  const [wordsNotCount, setWordsNotCount] = React.useState(0);
  const [isOverMaxWordsNot, setIsOverMaxWordsNot] = React.useState(false);
  const [errorMessageOverMaxWordsNot, setErrorMessageOverMaxWordsNot] = React.useState("");

  React.useEffect(() => {
    setWordsNotCount(countKeywords(state.words_not));
  }, [state.words_not]);

  React.useEffect(() => {
    setOverMaxMessage(maxWordsNot, wordsNotCount, setIsOverMaxWordsNot, setErrorMessageOverMaxWordsNot, messages.errorMsg.overMaxWordsNot);
  }, [wordsNotCount, maxWordsNot]);

  const handlePresetChange = (event) => {
    const selectedValue = event.target.value;
    if (!selectedValue) return;

    // 現在の入力値がある場合は改行して追加、なければそのまま挿入
    const newValue = state.words_and 
      ? `${state.words_and}\n${selectedValue}` 
      : selectedValue;

    // 親コンポーネントのステートを更新する処理を呼び出す
    // ※ changeWordsAndが event.target.value を参照していることを想定した模擬イベント
    changeWordsAnd({
      target: {
        name: 'words_and',
        value: newValue
      }
    });
  };

  return (
    <Grid item xs={12}>
      <FormControl fullWidth>
        <Typography variant="h6" gutterBottom>
          収集キーワード*
        </Typography>
        <Typography variant="body2">
          ※英字、数字、記号は、全角、半角、大文字、小文字の区別はしません。
        </Typography>
        <Typography variant="body2">
          ※AND条件は、,（カンマ）区切りで横に並べて入力してください。
        </Typography>
        <Typography variant="body2" gutterBottom>
          ※改行区切りすることで、追加のキーワードを入力することができます。詳細はマニュアルをご確認ください。
        </Typography>
        <TextField
          label="収集キーワード"
          required
          multiline
          minRows={4}
          maxRows={10}
          value={state.words_or}
          id="words_or"
          name="words_or"
          onChange={(event) => {
            changeWordsOr(event.target.value);
          }}
        />
      </FormControl>
      {isOverMaxWordsOr && (
        <Alert severity="error" className={classes.alert}>
          {errorMessageOverMaxWordsOr}
        </Alert>
      )}
      <Grid className={classes.wrapper} container justifyContent="flex-end">
        <Typography>収集キーワード数：</Typography>
        <Typography><span className={(isOverMaxWordsOr == false) ? classes.countDef : classes.countErr}>{wordsOrCount}</span> / {maxWordsOr}</Typography>
      </Grid>

      <Grid item xs={12} className={classes.accordionWrapper}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography>詳細設定</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid container spacing={3} className={classes.accordion}>
                <Grid item xs={12}>
                <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    width="100%"
                  >
                    <Typography variant="h6">
                      絞り込みキーワード
                    </Typography>
                    
                    {/* 2. プルダウンメニューの挿入 */}
                    <FormControl variant="outlined" size="small" style={{ marginLeft: '16px', minWidth: '250px' }}>
                      <InputLabel id="preset-select-label">絞り込みキーワードを挿入</InputLabel>
                      <Select
                        labelId="preset-select-label"
                        value=""
                        onChange={handlePresetChange}
                        label="絞り込みキーワードを挿入"
                      >
                        <MenuItem value=""><em>テーマ</em></MenuItem>
                        {presetKeywords.map((item) => (
                          <MenuItem key={item.label} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={changeIgnoreCaseAnd}
                        color="primary"
                        checked={state.ignore_case_and}
                      />
                    }
                    label="大文字/小文字・全角/半角を区別しない"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    ※複数キーワードを改行区切りで指定します
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="絞り込みキーワード"
                    name="words_and"
                    multiline
                    minRows={4}
                    maxRows={10}
                    variant="outlined"
                    fullWidth
                    onChange={changeWordsAnd}
                    value={state.words_and}
                  />
                  {isOverMaxWordsAnd && (
                    <Alert severity="error" className={classes.alert}>
                      {errorMessageOverMaxWordsAnd}
                    </Alert>
                  )}
                  <Grid className={classes.wrapper} container justifyContent="flex-end">
                    <Typography>絞り込みキーワード数：</Typography>
                    <Typography><span className={(isOverMaxWordsAnd == false) ? classes.countDef : classes.countErr}>{wordsAndCount}</span> / {maxWordsAnd}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={3} className={classes.accordion}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    除外キーワード
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="checkedB"
                        color="primary"
                        onChange={changeIgnoreCaseNot}
                        checked={state.ignore_case_not}
                      />
                    }
                    label="大文字/小文字・全角/半角を区別しない"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    ※複数キーワードを改行区切りで指定します
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="除外キーワード"
                    name="words_not"
                    multiline
                    minRows={4}
                    maxRows={10}
                    variant="outlined"
                    fullWidth
                    onChange={changeWordsNot}
                    value={state.words_not}
                  />
                  {isOverMaxWordsNot && (
                    <Alert severity="error" className={classes.alert}>
                      {errorMessageOverMaxWordsNot}
                    </Alert>
                  )}
                  <Grid className={classes.wrapper} container justifyContent="flex-end">
                    <Typography>除外キーワード数：</Typography>
                    <Typography><span className={(isOverMaxWordsNot == false) ? classes.countDef : classes.countErr}>{wordsNotCount}</span> / {maxWordsNot}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={3} className={classes.accordion}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    メンション
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    ※設定された収集キーワードがメンションのアカウント指定部分(@XXXXのXXXX(部分一致))にのみ含まれるツイートも収集対象とします
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        onChange={changeMentions}
                        checked={state.mentions}
                      />
                    }
                    label="メンションを含める"
                  />
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
}
